import { z } from "zod";
/** The width of each bar or ledger row. */
export const EnergyResolution = z.enum(["day", "week", "month"]);
/** A calendar period that runs from its start in the site's timezone up to now. */
export const EnergySince = z.enum(["week", "month", "year", "records"]);
const RANK = { day: 0, week: 1, month: 2, year: 3 };
/**
 * Which stretch of history to report, and at what resolution.
 *
 * Either the last N buckets ending with the current one, or every bucket since
 * the start of the current week, month or year, or since records began. The
 * client names the period and the server works out where it starts, because
 * "today" depends on the site's timezone and section 3 keeps that arithmetic
 * out of the client.
 *
 * Every combination that could exceed the 500-item ceiling of section 10 is
 * refused here rather than truncated, and so is a resolution coarser than its
 * period: a "month" bucket inside "since the start of this week" has no
 * meaning. `records` is monthly only, because it is the one period that grows
 * without bound.
 */
export const EnergyQuery = z
    .object({
    resolution: EnergyResolution,
    last: z.coerce.number().int().min(1).max(500).optional(),
    since: EnergySince.optional(),
})
    .strict()
    .refine((query) => (query.last === undefined) !== (query.since === undefined), {
    message: "Give exactly one of last or since.",
})
    .refine((query) => query.since === undefined ||
    (query.since === "records"
        ? query.resolution === "month"
        : RANK[query.resolution] <= RANK[query.since]), { message: "The resolution must fit inside the period." });
/**
 * Energy over one stretch of time, integrated from stored power readings.
 *
 * Every figure is over the recorded part of the stretch only. A reading that
 * never arrived is not guessed at, so a stretch with gaps reports less energy
 * than flowed, and says so: `recordedSeconds` against `expectedSeconds` is how
 * much of it the figures cover, and `complete` is false whenever any of it is
 * missing. A client must show that rather than let a partial total pass for a
 * whole one.
 *
 * The figures are null, never zero, when nothing at all was recorded. Zero is
 * a measurement, and a stretch with no readings has none.
 */
export const EnergyFigures = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    recordedSeconds: z.number().int(),
    expectedSeconds: z.number().int(),
    complete: z.boolean(),
    /** Delivered to the house from the inverter's output, whatever supplied it. */
    loadKwh: z.number().nullable(),
    gridImportKwh: z.number().nullable(),
    batteryChargeKwh: z.number().nullable(),
    batteryDischargeKwh: z.number().nullable(),
    /** Generated by the panels, both strings together (docs/DECISIONS/0028). */
    solarKwh: z.number().nullable(),
    /**
     * What the grid charger drew from the grid, as the meter bills it: the
     * charger's own loss of about 6% included (docs/DECISIONS/0033, the operator
     * on 2026-09-30). Part of `gridImportKwh`, not added to it.
     */
    gridChargeKwh: z.number().nullable(),
    /**
     * What the grid import cost, in the site's currency, each quarter hour at
     * the tariff in force then, including VAT. Null, never partial, when any
     * recorded part of the stretch had no tariff.
     */
    gridImportCost: z.number().nullable(),
    /** What the energy in `gridChargeKwh` cost, priced the same way. */
    gridChargeCost: z.number().nullable(),
});
/**
 * A derived energy figure set against the inverter's own lifetime counter for
 * the same flow, over exactly the same stretches of time.
 *
 * Two pairs are reconciled: battery discharge and solar. The inverter's other
 * two counters measure narrower things than their names suggest -- AC charging
 * only, and load served from the grid only -- so neither matches a figure
 * derived here once anything but the grid supplies the house
 * (docs/DECISIONS/0027, 0028).
 */
export const EnergyReconciliation = z.object({
    derivedKwh: z.number(),
    counterKwh: z.number(),
    differenceKwh: z.number(),
    toleranceKwh: z.number(),
    agrees: z.boolean(),
});
export const EnergyHistory = z.object({
    timezone: z.string(),
    resolution: EnergyResolution,
    totals: EnergyFigures,
    buckets: z.array(EnergyFigures),
    /** Null when no stretch of the period carried the counter on both sides. */
    dischargeCheck: EnergyReconciliation.nullable(),
    /** The same, against the lifetime solar counter. */
    solarCheck: EnergyReconciliation.nullable(),
});
/**
 * Where the energy the battery gave out came from, and what it had cost to
 * put in, over one stretch (docs/DECISIONS/0033).
 *
 * The battery is treated as a stock: charge adds to it by source, discharge
 * draws every source down in proportion, and energy the battery loses keeps
 * its cost, so `dischargedCost` includes the round trip's loss. Unattributed
 * energy is what was in the battery when records began, or appeared across a
 * gap; it drains as the battery cycles.
 */
export const BatteryFigures = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    chargedKwh: z.number().nullable(),
    dischargedKwh: z.number().nullable(),
    fromGridKwh: z.number().nullable(),
    fromSolarKwh: z.number().nullable(),
    unattributedKwh: z.number().nullable(),
    /** Null when grid energy no tariff priced was among what was drawn. */
    dischargedCost: z.number().nullable(),
    /**
     * `dischargedCost` over `fromGridKwh`: what a kWh charged from the grid cost
     * once it came back out, above the tariff by the round trip's loss. Null
     * when none of the discharge came from the grid, or its cost is unknown.
     */
    gridCostPerKwh: z.number().nullable(),
    /**
     * Discharge over charge. Null until `requiredChargeKwh` has been charged
     * within the stretch: the energy held at either end is not corrected for,
     * because the battery's SOC cannot be trusted to, and ten capacities keeps
     * that error under 10%.
     */
    roundTripEfficiency: z.number().nullable(),
    requiredChargeKwh: z.number(),
});
export const BatteryHistory = z.object({
    timezone: z.string(),
    resolution: EnergyResolution,
    capacityKwh: z.number(),
    totals: BatteryFigures,
    buckets: z.array(BatteryFigures),
});
//# sourceMappingURL=energy.js.map